import { useCallback, useMemo, useState } from "react";

import clsx from "clsx";
import { useToasts } from "react-toast-notifications";
import { useLocalStorage } from "usehooks-ts";

import salesChartLoading from "@assets/sales-v3-chart-loading.gif";
import { TitleSlide } from "@components/title-slide-view";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { AdLabelMatches } from "@features/breakdown-v2/ad-label-matches";
import { LabelRuleWizard } from "@features/breakdown-v2/label-rule-wizard/components";
import { LabelWizardProvider } from "@features/breakdown-v2/label-rule-wizard/context";
import { useLabelWizard } from "@features/breakdown-v2/label-rule-wizard/hooks";
import {
  CREATE_LABEL_RULE_GROUP_V3,
  GET_LABEL_RULE_GROUPS_V3,
  GET_LABEL_WIZARD_DATA_V3,
} from "@gql/label-rules-v3";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type CreateLabelRuleGroupV3,
  type CreateLabelRuleGroupV3Variables,
} from "@north-beam/nb-client-side";
import {
  breakdownGroupToLabelRules,
  createEmptyBreakdownGroup,
  getNumberOfErrors,
  validateBreakdownGroup,
  type BreakdownGroupDescriptor,
  type SegmentDescriptor,
} from "@north-beam/nb-common";
import { PrimaryButton, TertiaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { notifyLabelsCache } from "@shared/notify-labels-cache";
import Spinner from "@shared/spinner";

import {
  AdvancedLabelRuleWizard,
  type AdvancedSegmentToEdit,
} from "../breakdowns/components/edit-breakdown/advanced-editor/advanced-label-rule-wizard";
import { type AdvancedBreakdownDescriptor } from "../breakdowns/components/edit-breakdown/advanced-editor/types";
import {
  advancedBreakdownToLabelRules,
  createEmptyAdvancedBreakdown,
  simpleBreakdownToAdvancedBreakdown,
  validateAdvancedBreakdown,
} from "../breakdowns/components/edit-breakdown/advanced-editor/utils";
import { SwitchCreatorComfirmationModal } from "../breakdowns/components/edit-breakdown/switch-editor-confirmation-modal";

import { AdvancedSegmentForm } from "./advanced-segment-form";
import {
  DefaultBreakdowns,
  type PopularBreakdownOption,
} from "./default-breakdowns";
import { TutorialHeader } from "./tutorial-header";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export const CreateBreakdownModal = (props: Props) => {
  return (
    <LabelWizardProvider>
      <Content {...props} />
    </LabelWizardProvider>
  );
};

const Content = (props: Props) => {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { labelNames, labelFilters, loading } = useLabelWizard();

  const [createLabelRuleGroup, { loading: saving }] = useNorthbeamMutation<
    CreateLabelRuleGroupV3,
    CreateLabelRuleGroupV3Variables
  >(CREATE_LABEL_RULE_GROUP_V3, {
    useCpuIntensive: true,
    refetchQueries: [
      { query: GET_LABEL_RULE_GROUPS_V3 },
      { query: GET_LABEL_WIZARD_DATA_V3 },
    ],
    awaitRefetchQueries: true,
  });

  const [showTutorialHeader, setShowTutorialHeader] = useLocalStorage(
    "createBreakdownModalShowTutorialHeader",
    true,
  );

  const [aboutToClose, setAboutToClose] = useState(false);
  const [segmentToPreview, setSegmentToPreview] =
    useState<SegmentDescriptor | null>(null);
  const [isBreakdownModified, setIsBreakdownModified] = useState(false);
  const [isSimpleUI, setSimpleUI] = useState(true);
  const [isRaw, setIsRaw] = useState(false);

  const [selectedPopularBreakdown, setSelectedPopularBreakdown] =
    useState<PopularBreakdownOption | null>(null);
  const [breakdownGroup, setBreakdownGroup] =
    useState<BreakdownGroupDescriptor>(createEmptyBreakdownGroup());
  const [advancedBreakdown, setAdvancedBreakdown] =
    useState<AdvancedBreakdownDescriptor>(() => createEmptyAdvancedBreakdown());
  const [segmentToEdit, setSegmentToEdit] = useState<AdvancedSegmentToEdit>({
    isNew: true,
    segment: null,
  });

  const { addToast } = useToasts();

  const validateAndUpdateBreakdownGroup = useCallback(
    (value: BreakdownGroupDescriptor) => {
      if (getNumberOfErrors(value) > 0) {
        setBreakdownGroup(validateBreakdownGroup(value, labelNames));
      } else {
        setBreakdownGroup(value);
      }
    },
    [labelNames],
  );

  const updateAdvancedBreakdown = (breakdown: AdvancedBreakdownDescriptor) => {
    setAdvancedBreakdown(breakdown);
    setIsBreakdownModified(true);
  };

  const [showSwitchEditorConfirmModal, setShowSwitchEditorConfirmModal] =
    useState(false);

  const onClose = () => {
    setSegmentToPreview(null);
    setSelectedPopularBreakdown(null);
    setAboutToClose(false);
    setBreakdownGroup(createEmptyBreakdownGroup());
    setIsBreakdownModified(false);
    setSimpleUI(true);
    props.onClose();
  };

  const errorsCount = useMemo(() => {
    if (isSimpleUI) {
      return getNumberOfErrors(breakdownGroup);
    } else {
      return advancedBreakdown.error ? 1 : 0;
    }
  }, [breakdownGroup, advancedBreakdown, isSimpleUI]);

  return (
    <>
      <Modal
        size={aboutToClose ? "xl" : "3xl"}
        {...props}
        bodyClassName="px-0 py-0"
        hideCloseButton={!!segmentToPreview}
        titleContent={
          aboutToClose ? (
            <div className="modal__title">
              <h3 className="heading mx-0 mb-0 mt-0 font-bold text-[1.5rem] leading-8">
                Exit without saving your breakdown?
              </h3>
            </div>
          ) : (
            <>
              {segmentToPreview && (
                <div>
                  <TertiaryButton
                    className={clsx(
                      "border-gray-0 border-solid border flex items-center justify-center gap-2 mb-5",
                    )}
                    onClick={() => {
                      prettyLogEventPage({
                        domain: "Breakdown v2",
                        action: "onClick",
                        additionalActionInfo: "Close matches preview",
                      });

                      setSegmentToPreview(null);
                    }}
                  >
                    <i className="fa-regular fa-arrow-left flex items-center justify-center"></i>
                    <span className="text-black uppercase font-semibold">
                      Back
                    </span>
                  </TertiaryButton>
                  <h3
                    className={clsx(
                      "heading mx-0 mb-0 mt-0 font-bold text-[1.5rem] max-w-[900px]",
                      "flex items-center gap-2 overflow-hidden text-ellipsis whitespace-nowrap",
                    )}
                  >
                    Matches:
                    <span className="font-normal flex items-center gap-2">
                      {breakdownGroup.name}
                      <i className="fa-regular fa-angle-right text-lg mt-0.5" />
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap max-w-[500px]">
                        {segmentToPreview.name}
                      </span>
                    </span>
                  </h3>
                </div>
              )}
              {!(segmentToPreview || segmentToEdit.segment) && (
                <div className="modal__title">
                  <h3 className="heading mx-0 mb-0 mt-0 font-bold text-[1.5rem]">
                    Create a breakdown
                  </h3>
                </div>
              )}
              {segmentToEdit.segment && (
                <div>
                  <TertiaryButton
                    className={clsx(
                      "border-gray-0 border-solid border flex items-center justify-center gap-2 mb-5",
                    )}
                    onClick={() => {
                      prettyLogEventPage({
                        domain: "Breakdown v2",
                        action: "onClick",
                        additionalActionInfo: "Edit segment",
                      });

                      setSegmentToEdit({ isNew: true, segment: null });
                    }}
                  >
                    <i className="fa-regular fa-arrow-left flex items-center justify-center"></i>
                    <span className="text-black uppercase font-semibold">
                      Back
                    </span>
                  </TertiaryButton>
                  <h3
                    className={clsx(
                      "heading mx-0 mb-0 mt-0 font-bold text-[1.5rem] max-w-[900px]",
                      "overflow-hidden text-ellipsis whitespace-nowrap",
                    )}
                  >
                    Editing rule for “{advancedBreakdown.name}” breakdown
                  </h3>
                </div>
              )}
            </>
          )
        }
        onClose={() => {
          if (isBreakdownModified) {
            if (aboutToClose) {
              setAboutToClose(false);
            } else {
              setAboutToClose(true);
            }
          } else {
            onClose();
          }
        }}
      >
        {loading ? (
          <TitleSlide className="p-12">
            <img
              src={salesChartLoading}
              alt="Loading spinner"
              className="w-1/5 m-auto"
            />
          </TitleSlide>
        ) : (
          <div className={clsx({ "p-5": !(aboutToClose || segmentToPreview) })}>
            {aboutToClose ? (
              <div className="text-sm text-gray+1 flex items-center justify-between px-5 mb-2">
                The edits you’ve made will be lost.
              </div>
            ) : (
              <>
                {isSimpleUI ? (
                  <>
                    {segmentToPreview && (
                      <div className="pt-3 px-5">
                        <AdLabelMatches
                          segment={segmentToPreview}
                          segments={breakdownGroup.segments}
                        />
                      </div>
                    )}
                    {!segmentToPreview && (
                      <div className="mb-8">
                        {showTutorialHeader ? (
                          <div className="mb-4">
                            <TutorialHeader
                              onDismiss={() => {
                                prettyLogEventPage({
                                  domain: "Breakdown v2",
                                  action: "onClick",
                                  additionalActionInfo: "Hide tutorial",
                                });

                                setShowTutorialHeader(false);
                              }}
                            />
                          </div>
                        ) : (
                          <div className="h-[72px]"></div>
                        )}
                        <div className="border-b border-gray-1 mt-2 mb-8 pb-4">
                          <DefaultBreakdowns
                            labelFilters={labelFilters}
                            showConfirmation={isBreakdownModified}
                            selected={selectedPopularBreakdown}
                            onSelect={(name, group) => {
                              prettyLogEventPage({
                                domain: "Breakdown v2",
                                action: "onClick",
                                additionalActionInfo:
                                  "Select default breakdown",
                                data: {
                                  pageContext: { name, breakdown: group },
                                },
                              });

                              setSelectedPopularBreakdown(name);
                              validateAndUpdateBreakdownGroup(group);
                              setIsBreakdownModified(false);
                            }}
                          />
                        </div>
                        <LabelRuleWizard
                          breakdownGroup={breakdownGroup}
                          onChange={(value) => {
                            validateAndUpdateBreakdownGroup(value);
                            setIsBreakdownModified(true);
                          }}
                          onClickShowMatches={(value) => {
                            prettyLogEventPage({
                              domain: "Breakdown v2",
                              action: "onClick",
                              additionalActionInfo: "Preview matches",
                              data: {
                                pageContext: { segment: value },
                              },
                            });

                            setSegmentToPreview(value);
                          }}
                          onSwitchToAdvancedEditor={() => {
                            setShowSwitchEditorConfirmModal(true);
                          }}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {segmentToEdit.segment ? (
                      <AdvancedSegmentForm
                        isUpdate={!segmentToEdit.isNew}
                        breakdown={advancedBreakdown}
                        segment={segmentToEdit.segment}
                        onCancel={() => {
                          prettyLogEventPage({
                            domain: "Breakdown v2",
                            action: "onClick",
                            additionalActionInfo: "Cancel segment edit",
                          });

                          setSegmentToEdit({ isNew: true, segment: null });
                        }}
                        onUpdate={(segment) => {
                          prettyLogEventPage({
                            domain: "Breakdown v2",
                            action: "onClick",
                            additionalActionInfo: "Update segment",
                            data: {
                              pageContext: { segment },
                            },
                          });

                          if (
                            advancedBreakdown.segments
                              .map((e) => e.id)
                              .includes(segment.id)
                          ) {
                            updateAdvancedBreakdown({
                              ...advancedBreakdown,
                              segments: advancedBreakdown.segments.map((e) =>
                                e.id === segment.id ? segment : e,
                              ),
                            });
                          } else {
                            updateAdvancedBreakdown({
                              ...advancedBreakdown,
                              segments: [
                                ...advancedBreakdown.segments,
                                segment,
                              ],
                            });
                          }

                          setSegmentToEdit({ isNew: true, segment: null });
                        }}
                      />
                    ) : (
                      <AdvancedLabelRuleWizard
                        advancedBreakdown={advancedBreakdown}
                        isRaw={isRaw}
                        onChange={(value) => {
                          prettyLogEventPage({
                            domain: "Breakdown v2",
                            action: "onClick",
                            additionalActionInfo: "Update breakdown",
                            data: {
                              pageContext: { breakdown: value },
                            },
                          });

                          updateAdvancedBreakdown(value);
                        }}
                        onEditSegment={(value) => {
                          prettyLogEventPage({
                            domain: "Breakdown v2",
                            action: "onClick",
                            additionalActionInfo: "Edit segment",
                            data: {
                              pageContext: { segment: value },
                            },
                          });

                          setSegmentToEdit(value);
                        }}
                        onChangeRaw={(value) => {
                          prettyLogEventPage({
                            domain: "Breakdown v2",
                            action: "onClick",
                            additionalActionInfo: "Switch to raw mode",
                            data: {
                              pageContext: { breakdown: value },
                            },
                          });

                          setIsRaw(value);
                        }}
                      />
                    )}
                  </>
                )}
              </>
            )}
            <>
              {aboutToClose ? (
                <div className="text-sm flex items-center justify-end gap-2 px-5 py-4">
                  <div className="gap-2 flex">
                    <TertiaryButton
                      className="ml-2 font-bold uppercase"
                      onClick={() => setAboutToClose(false)}
                    >
                      No, Return to 'Create a Breakdown'
                    </TertiaryButton>
                    <PrimaryButton
                      className="font-bold uppercase"
                      onClick={() => onClose()}
                    >
                      Yes, exit without saving
                    </PrimaryButton>
                  </div>
                </div>
              ) : (
                <>
                  {!loading && !segmentToPreview && !segmentToEdit.segment && (
                    <>
                      <div className="text-sm flex items-center justify-end gap-12 pt-4">
                        {errorsCount > 0 && (
                          <div className="flex-1 flex items-center gap-3 rounded-md bg-mars-2 h-8">
                            <div
                              className={clsx(
                                "text-white flex items-center justify-center px-3 bg-mars rounded-l-md",
                                "h-full",
                              )}
                            >
                              <i className="fa-regular fa-sharp fa-exclamation-circle"></i>
                            </div>
                            Resolve ({errorsCount}) errors before saving
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          {!saving && (
                            <TertiaryButton
                              className="font-bold"
                              onClick={() => {
                                if (isBreakdownModified) {
                                  setAboutToClose(true);
                                } else {
                                  onClose();
                                }
                              }}
                            >
                              CLOSE
                            </TertiaryButton>
                          )}
                          <PrimaryButton
                            disabled={
                              getNumberOfErrors(breakdownGroup) > 0 || saving
                            }
                            className="font-bold flex items-center gap-2"
                            onClick={async () => {
                              prettyLogEventPage({
                                domain: "Breakdown v2",
                                action: "onClick",
                                additionalActionInfo: "Save breakdown",
                              });

                              if (isSimpleUI) {
                                const validatedGroup = validateBreakdownGroup(
                                  breakdownGroup,
                                  labelNames,
                                );
                                setBreakdownGroup(validatedGroup);

                                if (getNumberOfErrors(validatedGroup) === 0) {
                                  const labelRules = breakdownGroupToLabelRules(
                                    validatedGroup,
                                    labelFilters,
                                  );

                                  await createLabelRuleGroup({
                                    variables: {
                                      campaignLabelRule: labelRules.campaign,
                                      adsetLabelRule: labelRules.adset,
                                      adLabelRule: labelRules.ad,
                                    },
                                  });

                                  addToast(
                                    `Breakdown "${validatedGroup.name}" created`,
                                    {
                                      appearance: "success",
                                    },
                                  );
                                  notifyLabelsCache();
                                  onClose();
                                }
                              } else {
                                const validatedAdvancedBreakdown =
                                  validateAdvancedBreakdown(
                                    advancedBreakdown,
                                    labelNames,
                                  );

                                if (validatedAdvancedBreakdown.error) {
                                  updateAdvancedBreakdown(
                                    validatedAdvancedBreakdown,
                                  );
                                } else {
                                  const result =
                                    advancedBreakdownToLabelRules(
                                      advancedBreakdown,
                                    );

                                  await createLabelRuleGroup({
                                    variables: {
                                      campaignLabelRule: result.campaign,
                                      adsetLabelRule: result.adset,
                                      adLabelRule: result.ad,
                                    },
                                  });

                                  addToast(
                                    `Breakdown "${validatedAdvancedBreakdown.name}" updated`,
                                    {
                                      appearance: "success",
                                    },
                                  );
                                  notifyLabelsCache();
                                  onClose();
                                }
                              }
                            }}
                          >
                            {saving && (
                              <Spinner colorClassname="text-white-400" />
                            )}
                            SAVE
                          </PrimaryButton>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </>
          </div>
        )}
      </Modal>
      <SwitchCreatorComfirmationModal
        isOpen={showSwitchEditorConfirmModal}
        breakdownGroup={breakdownGroup}
        onClose={() => {
          prettyLogEventPage({
            domain: "Breakdown v2",
            action: "onClick",
            additionalActionInfo:
              "Close switch to advanced editor confirmation modal",
          });

          setShowSwitchEditorConfirmModal(false);
        }}
        onConfirm={() => {
          prettyLogEventPage({
            domain: "Breakdown v2",
            action: "onClick",
            additionalActionInfo: "Switch to advanced editor",
          });

          const advBreakdown = simpleBreakdownToAdvancedBreakdown(
            breakdownGroup,
            labelFilters,
          );
          setAdvancedBreakdown(advBreakdown);
          setSimpleUI(false);
          setShowSwitchEditorConfirmModal(false);
        }}
      />
    </>
  );
};
